import React, { useEffect, useState, useRef } from 'react';
import { Target, Camera, Vote, MessageSquarePlus, Crown, Glasses, Plus, X, Link2, Flame, Coins, Shirt, Gift, Aperture, Scan, RefreshCw, Diamond, Sparkles, Heart, FileText, Star, Trophy } from 'lucide-react';
import { PhotoRatingFlow } from './PhotoRatingFlow';
import { EnhancedSpinWheel } from './EnhancedSpinWheel';
import { RitualIcon } from './icons/RitualIcon';
import { DeepShiftModal } from './ai-shift/DeepShiftModal';
import { FaceShiftModal } from './ai-shift/FaceShiftModal';
import { ShapeShiftModal } from './shape-shift/ShapeShiftModal';
interface FloatingActionButtonProps {
  onActionSelect: (action: string) => void;
  angelContext?: string; // Optional: Name of angel if on an angel feed
  hideInCreatorStudio?: boolean; // New prop to control visibility in Creator Studio
  onBuyTokens?: () => void; // Prop to handle opening the token store
}
export const FloatingActionButton: React.FC<FloatingActionButtonProps> = ({
  onActionSelect,
  angelContext,
  hideInCreatorStudio = false,
  onBuyTokens
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const [showPhotoRatingFlow, setShowPhotoRatingFlow] = useState(false);
  const [showEnhancedSpinWheel, setShowEnhancedSpinWheel] = useState(false);
  const [showDeepShiftModal, setShowDeepShiftModal] = useState(false);
  const [showFaceShiftModal, setShowFaceShiftModal] = useState(false);
  const [showShapeShiftModal, setShowShapeShiftModal] = useState(false);
  const [currentRitualId, setCurrentRitualId] = useState<string | null>(null);
  const actionButtonRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState({
    top: 0,
    left: 0
  });
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };
  const handleActionClick = (action: string) => {
    if (action === 'dickpic_challenge') {
      setShowPhotoRatingFlow(true);
    } else if (action === 'buy_tokens_bottom' && onBuyTokens) {
      onBuyTokens();
    } else if (action === 'fortune_wheel') {
      setShowEnhancedSpinWheel(true);
    } else if (action === 'deep_shift_tm') {
      setShowDeepShiftModal(true);
    } else if (action === 'face_shift_tm') {
      setShowFaceShiftModal(true);
    } else if (action === 'shape_shift_tm') {
      setShowShapeShiftModal(true);
    } else {
      onActionSelect(action);
    }
    setIsOpen(false);
  };
  // Notification counts for each action
  const notifications = {
    total: 15,
    daily_rituals: 3,
    dickpic_challenge: 3,
    relationship: 3,
    social_polls: 0,
    fan_club: 0,
    ar_vr: 0,
    hot_or_not: 5,
    buy_tokens_bottom: 0,
    angel_skins: 1,
    fortune_wheel: 1,
    deep_shift_tm: 1,
    face_shift_tm: 1,
    shape_shift_tm: 1
  };
  const actions = [{
    id: 'ar_vr',
    label: 'AR/VR Mode',
    icon: <Glasses size={20} data-id="element-7677" />,
    color: 'bg-blue-500',
    badge: notifications.ar_vr
  }, {
    id: 'fan_club',
    label: 'Fan Club',
    icon: <Heart size={20} data-id="element-7678" />,
    color: 'bg-pink-500',
    badge: notifications.fan_club
  }, {
    id: 'social_polls',
    label: 'Social Polls',
    icon: <FileText size={20} data-id="element-7679" />,
    color: 'bg-purple-500',
    badge: notifications.social_polls
  }, {
    id: 'candy_crusher',
    label: 'CandyCrusher',
    icon: <Gift size={20} data-id="element-7680" />,
    color: 'bg-orange-500',
    badge: 0
  }, {
    id: 'relationship',
    label: 'Relationship',
    icon: <Heart size={20} data-id="element-7681" />,
    color: 'bg-red-500',
    badge: notifications.relationship
  }, {
    id: 'angel_skins',
    label: 'Angel Skins',
    icon: <Sparkles size={20} data-id="element-7682" />,
    color: 'bg-indigo-500',
    badge: notifications.angel_skins
  }, {
    id: 'fortune_wheel',
    label: 'Fortune Wheel',
    icon: <Star size={20} data-id="element-7683" />,
    color: 'bg-yellow-500',
    badge: notifications.fortune_wheel
  }, {
    id: 'hot_or_not',
    label: 'Hot or Not',
    icon: <Flame size={20} data-id="element-7684" />,
    color: 'bg-red-500',
    badge: notifications.hot_or_not
  }, {
    id: 'dickpic_challenge',
    label: 'DickPic Challenge',
    icon: <Camera size={20} data-id="element-7685" />,
    color: 'bg-purple-500',
    badge: notifications.dickpic_challenge
  }, {
    id: 'daily_rituals',
    label: 'Daily Rituals',
    icon: <Trophy size={20} data-id="element-7686" />,
    color: 'bg-green-500',
    badge: notifications.daily_rituals
  }, {
    id: 'deep_shift_tm',
    label: 'DeepShift™',
    icon: <Sparkles size={20} data-id="element-7687" />,
    color: 'bg-pink-500',
    badge: notifications.deep_shift_tm
  }, {
    id: 'face_shift_tm',
    label: 'FaceShift™',
    icon: <Sparkles size={20} data-id="element-7688" />,
    color: 'bg-blue-500',
    badge: notifications.face_shift_tm
  }, {
    id: 'shape_shift_tm',
    label: 'ShapeShift™',
    icon: <Sparkles size={20} data-id="element-7689" />,
    color: 'bg-purple-500',
    badge: notifications.shape_shift_tm
  }, {
    id: 'buy_tokens_bottom',
    label: 'Buy Tokens',
    icon: <Diamond size={20} data-id="element-7690" />,
    color: 'bg-green-500',
    badge: 0
  }];
  // Add event listeners for ShapeShift modal
  useEffect(() => {
    const handleOpenShapeShiftModal = (event: any) => {
      if (event.detail && event.detail.ritualId) {
        setCurrentRitualId(event.detail.ritualId);
      }
      setShowShapeShiftModal(true);
    };
    document.addEventListener('openShapeShiftModal', handleOpenShapeShiftModal);
    return () => {
      document.removeEventListener('openShapeShiftModal', handleOpenShapeShiftModal);
    };
  }, []);
  // Handle completion of ShapeShift
  const handleShapeShiftComplete = (result: any) => {
    setShowShapeShiftModal(false);
    // Dispatch ShapeShift completion event
    const event = new CustomEvent('shapeShiftComplete', {
      detail: {
        result,
        ritualId: currentRitualId
      }
    });
    document.dispatchEvent(event);
    // Check for ritual completion
    if (currentRitualId) {
      const ritualEvent = new CustomEvent('ritualCompleted', {
        detail: {
          ritualId: currentRitualId,
          type: 'shape_shift'
        }
      });
      document.dispatchEvent(ritualEvent);
    }
  };
  // If we should hide in creator studio, return null (don't render anything)
  if (hideInCreatorStudio) {
    return null;
  }
  return <>
      <div className="fixed bottom-6 right-6 z-30 flex flex-col-reverse items-end gap-2" data-id="element-7691">
        {isOpen && <div className="flex flex-col-reverse gap-2 animate-in slide-in-from-bottom duration-300" data-id="element-7692">
            {actions.map(action => <button key={action.id} onClick={() => {
          if (action.id === 'fan_club') {
            const navigateEvent = new CustomEvent('navigate', {
              detail: {
                section: 'fanclub'
              }
            });
            document.dispatchEvent(navigateEvent);
          } else if (action.id === 'daily_rituals') {
            const navigateEvent = new CustomEvent('navigate', {
              detail: {
                section: 'rituals'
              }
            });
            document.dispatchEvent(navigateEvent);
            setIsOpen(false);
          } else if (action.id === 'relationship') {
            const navigateEvent = new CustomEvent('navigate', {
              detail: {
                section: 'relationship_tracking'
              }
            });
            document.dispatchEvent(navigateEvent);
          } else if (action.id === 'social_polls') {
            const navigateEvent = new CustomEvent('navigate', {
              detail: {
                section: 'social_polls'
              }
            });
            document.dispatchEvent(navigateEvent);
            setIsOpen(false);
          } else if (action.id === 'hot_or_not') {
            const navigateEvent = new CustomEvent('navigate', {
              detail: {
                section: 'hot_or_not'
              }
            });
            document.dispatchEvent(navigateEvent);
            setIsOpen(false);
          } else if (action.id === 'candy_crusher') {
            // Navigate to Games section for CandyCrusher
            const navigateEvent = new CustomEvent('navigate', {
              detail: {
                section: 'games'
              }
            });
            document.dispatchEvent(navigateEvent);
            setIsOpen(false);
          } else if (action.id === 'buy_tokens_bottom') {
            // Even if onBuyTokens is not provided, navigate to token_wallet
            if (onBuyTokens) {
              onBuyTokens();
            } else {
              // Dispatch a navigation event to the token_wallet section
              const navigateEvent = new CustomEvent('navigate', {
                detail: {
                  section: 'token_wallet'
                }
              });
              document.dispatchEvent(navigateEvent);
            }
            setIsOpen(false);
          } else if (action.id === 'angel_skins') {
            // Navigate to Angel Skins Shop
            const navigateEvent = new CustomEvent('navigate', {
              detail: {
                section: 'angel_skins_shop'
              }
            });
            document.dispatchEvent(navigateEvent);
            setIsOpen(false);
          } else {
            handleActionClick(action.id);
          }
        }} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-md hover:shadow-lg transition-all group relative" data-id="element-7693">
                <div className="w-8 h-8 flex items-center justify-center bg-gray-100 dark:bg-gray-800 group-hover:bg-pink-100 dark:group-hover:bg-pink-900/20 rounded-full transition-colors" data-id="element-7694">
                  {action.id === 'daily_rituals' ? <RitualIcon size={20} state={action.badge > 0 ? 'active' : 'default'} data-id="element-7695" /> : action.icon}
                </div>
                <span className="text-sm font-medium" data-id="element-7696">{action.label}</span>
                {action.badge > 0 && <span className="ml-1 flex items-center justify-center min-w-5 h-5 bg-pink-500 text-white text-xs font-medium rounded-full px-1.5" data-id="element-7697">
                    {action.badge}
                  </span>}
              </button>)}
          </div>}
        <button onClick={toggleMenu} className={`w-16 h-16 rounded-full flex items-center justify-center shadow-xl hover:shadow-2xl transition-all relative ${isOpen ? 'bg-gray-800 dark:bg-gray-700' : 'bg-gradient-to-r from-pink-500 to-purple-600 ring-4 ring-pink-500/30 dark:ring-purple-500/30'}`} data-id="element-7698">
          {isOpen ? <X size={28} className="text-white" data-id="element-7699" /> : <>
              <Plus size={28} className="text-white" data-id="element-7700" />
              <span className="absolute -top-2 -right-2 flex items-center justify-center min-w-6 h-6 bg-pink-500 text-white text-xs font-bold rounded-full px-1.5 animate-pulse" data-id="element-7701">
                15
              </span>
            </>}
        </button>
      </div>

      {/* PhotoRatingFlow Modal */}
      {showPhotoRatingFlow && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-7702">
          <PhotoRatingFlow onClose={() => setShowPhotoRatingFlow(false)} data-id="element-7703" />
        </div>}

      {/* Enhanced Spin Wheel Modal */}
      {showEnhancedSpinWheel && <EnhancedSpinWheel isOpen={showEnhancedSpinWheel} onClose={() => setShowEnhancedSpinWheel(false)} onRewardClaimed={reward => {
      console.log('Claimed reward:', reward);
      // Handle reward claiming logic
      setShowEnhancedSpinWheel(false);
    }} userTokens={{
      loveTokens: 240,
      desireGems: 120 // Using the gem count from AngelSkinsShop.tsx
    }} data-id="element-7704" />}

      {/* Deep Shift Modal */}
      {showDeepShiftModal && <DeepShiftModal onClose={() => setShowDeepShiftModal(false)} onComplete={result => {
      console.log('Deep Shift render complete:', result);
      // Handle render completion, update RewardHub, etc.
      setShowDeepShiftModal(false);
      // Dispatch event to update RewardHub
      const rewardEvent = new CustomEvent('aiCreationComplete', {
        detail: {
          type: 'deep_shift',
          result
        }
      });
      document.dispatchEvent(rewardEvent);
      // Trigger ritual completion
      const ritualEvent = new CustomEvent('ritualAction', {
        detail: {
          action: 'ai_creation',
          subtype: 'deep_shift'
        }
      });
      document.dispatchEvent(ritualEvent);
    }} ritualId={currentRitualId} data-id="element-7705" />}

      {/* Face Shift Modal */}
      {showFaceShiftModal && <FaceShiftModal onClose={() => setShowFaceShiftModal(false)} onComplete={result => {
      console.log('Face Shift render complete:', result);
      // Handle render completion, update RewardHub, etc.
      setShowFaceShiftModal(false);
      // Dispatch event to update RewardHub
      const rewardEvent = new CustomEvent('aiCreationComplete', {
        detail: {
          type: 'face_shift',
          result
        }
      });
      document.dispatchEvent(rewardEvent);
      // Trigger ritual completion
      const ritualEvent = new CustomEvent('ritualAction', {
        detail: {
          action: 'ai_creation',
          subtype: 'face_shift'
        }
      });
      document.dispatchEvent(ritualEvent);
    }} ritualId={currentRitualId} data-id="element-7706" />}

      {/* Shape Shift Modal */}
      {showShapeShiftModal && <ShapeShiftModal isOpen={showShapeShiftModal} onClose={() => setShowShapeShiftModal(false)} onComplete={handleShapeShiftComplete} ritualId={currentRitualId} angelName={angelContext} data-id="element-7707" />}
    </>;
};